import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { fetchMarketRenewalRisk, MarketModuleOffError } from '@/lib/marketExperienceApi';
import {
  asList,
  formatEnrolment,
  formatMomentum,
  formatCategoryLabel,
  getRenewalRiskMeta,
  sortRenewalRiskRows,
  rowSourceGap,
  mktLabel,
} from '@/lib/marketExperience';
import { useTranslation } from '@/components/utils/translations';
import MarketHelpLabel from './MarketHelpLabel';

function countByLevel(rows, level) {
  return rows.filter((r) => (r.risk_level || '') === level).length;
}

function capitalizeFirst(text) {
  const s = String(text || '');
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function whyLine(row) {
  if (row?.risk_detail) return capitalizeFirst(row.risk_detail);
  const reason = (row.risk_reasons || [])[0];
  if (reason?.detail) return capitalizeFirst(reason.detail);
  if (row?.risk_quote) return capitalizeFirst(row.risk_quote);
  if (reason?.quote) return capitalizeFirst(reason.quote);
  return '—';
}

function whySourceUrl(row) {
  if (row?.risk_source_url) return row.risk_source_url;
  const reason = (row.risk_reasons || [])[0];
  return reason?.source_url || reason?.url || reason?.evidence_url || null;
}

function RiskBadge({ row }) {
  const { t } = useTranslation();
  const meta = getRenewalRiskMeta(row?.risk_level);
  const label = row?.risk_label || meta.label;
  const extra = Number(row?.risk_reason_count);
  const showPlus = !Number.isNaN(extra) && extra > 1;
  return (
    <Badge className={`text-[11px] font-medium ${meta.className}`}>
      {mktLabel(t, label)}
      {showPlus ? <span className="ml-1 opacity-80">+{extra}</span> : null}
    </Badge>
  );
}

function SourceGap({ row }) {
  const { t } = useTranslation();
  const gap = rowSourceGap(row);
  if (gap === 'clients_only') {
    return <p className="text-[11px] text-amber-800">{t('mktGapClientsOnly')}</p>;
  }
  if (gap === 'stack_only') {
    return <p className="text-[11px] text-[#5C6B7A]">{t('mktGapStackOnly')}</p>;
  }
  return null;
}

function RiskDrawer({ open, onOpenChange, row }) {
  const { t } = useTranslation();
  const reasons = asList(row?.risk_reasons);
  const competitors = asList(row?.competitors_present);
  const meta = getRenewalRiskMeta(row?.risk_level);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-[#0A1A2F] pr-6">{row?.account_name || t('account')}</SheetTitle>
          <SheetDescription>
            {row?.country || ''}
            {row?.enrolment != null ? ` · ${formatEnrolment(row.enrolment, row.enrolment_measure)}` : ''}
          </SheetDescription>
        </SheetHeader>
        {row && (
          <div className="mt-6 space-y-4">
            <RiskBadge row={row} />
            <p className="text-xs text-[#5C6B7A]">{mktLabel(t, meta.means)}</p>
            <SourceGap row={row} />
            {(row.our_categories || []).length > 0 && (
              <p className="text-sm text-[#0A1A2F]">
                {t('mktOurCategoriesLine', { categories: (row.our_categories || []).map(formatCategoryLabel).join(', ') })}
              </p>
            )}
            {reasons.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs font-medium uppercase tracking-wide text-[#5C6B7A]">{t('mktWhy')}</p>
                {reasons.map((r, i) => (
                  <div key={i} className="rounded-md border border-gray-100 p-3 text-sm">
                    <p className="text-[#0A1A2F]">{capitalizeFirst(r.detail || r.kind)}</p>
                    {r.quote ? <p className="text-[#5C6B7A] italic mt-1">“{r.quote}”</p> : null}
                    <p className="text-[11px] text-[#5C6B7A] mt-1">
                      {r.category ? formatCategoryLabel(r.category) : ''}
                      {r.source_url ? (
                        <>
                          {r.category ? ' · ' : ''}
                          <a href={r.source_url} target="_blank" rel="noreferrer" className="text-[#00C2C2] hover:underline">{t('source')}</a>
                        </>
                      ) : null}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#5C6B7A]">{t('mktNoRenewalEvidence')}</p>
            )}
            {competitors.length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-[#5C6B7A] mb-1">{t('mktCompetitorsPresent')}</p>
                <ul className="text-sm text-[#0A1A2F] space-y-0.5">
                  {competitors.map((c, i) => (
                    <li key={i}>
                      {c.vendor || c}
                      {c.category ? ` · ${formatCategoryLabel(c.category)}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function MarketRenewalRisk({ tenantId, lens, countryFilter = 'all' }) {
  const { t } = useTranslation();
  const [payload, setPayload] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerRow, setDrawerRow] = useState(null);

  const country = countryFilter === 'all' ? undefined : countryFilter;

  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !lens) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setUnavailable(false);
    fetchMarketRenewalRisk(tenantId, lens, country)
      .then((data) => {
        if (cancelled) return;
        setPayload(data);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof MarketModuleOffError || e?.status === 404) {
          setUnavailable(true);
          setPayload(null);
        } else {
          setError(e?.message || 'Could not load renewal risk');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tenantId, lens, country]);

  const rows = useMemo(() => {
    // New API: rows are only at-risk. Older payloads may still include quiet/unknown — hide those.
    const list = (payload?.rows || []).filter((r) => {
      const level = r?.risk_level;
      if (level === 'quiet' || level === 'unknown') return false;
      return true;
    });
    return sortRenewalRiskRows(list);
  }, [payload]);

  const confirmed = payload?.levels?.confirmed ?? countByLevel(rows, 'confirmed');
  const watch = payload?.levels?.watch ?? countByLevel(rows, 'watch');
  const atRisk = payload?.at_risk ?? rows.length;
  const commentary = payload?.commentary;

  if (loading) return <div className="h-64 bg-gray-100 rounded-md animate-pulse" />;
  if (error) {
    return <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error === 'Could not load renewal risk' ? t('mktCouldNotLoadRenewalRisk') : error}</div>;
  }
  if (unavailable) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-6 space-y-2">
          <h2 className="text-lg font-semibold text-[#0A1A2F]">{t('mktWhoMightILose')}</h2>
          <p className="text-sm text-[#5C6B7A]">
            {t('mktRenewalNotSwitchedOn')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {commentary ? (
        <div className="rounded-lg border border-dashed border-stone-400 bg-[#F5F5F4] px-4 py-3">
          <p className="text-sm text-[#0A1A2F] leading-relaxed">{commentary}</p>
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { label: 'At risk', labelKey: 'sentimentAtRisk', value: atRisk, tone: 'default' },
          { label: 'Confirmed risk', labelKey: 'mktRiskConfirmed', value: confirmed, tone: 'default' },
          { label: 'Watch', labelKey: 'mktRiskWatch', value: watch, tone: 'default' },
        ].map((f) => (
          <div key={f.label} className="rounded-lg px-4 py-3 shadow-sm bg-white">
            <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A]">{t(f.labelKey)}</p>
            <p className="text-2xl font-semibold text-[#0A1A2F] mt-0.5">{f.value}</p>
          </div>
        ))}
      </div>

      <Card className="border-0 shadow-md">
        <CardContent className="p-4 md:p-6">
          {rows.length === 0 ? (
            <p className="text-sm text-[#5C6B7A] py-8 text-center">
              {country ? t('mktNoAtRiskForCountry', { country }) : t('mktNoAtRisk')}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs text-[#5C6B7A]">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('account')}</th>
                    <th className="px-3 py-2 font-medium">{t('mktOurCategories')}</th>
                    <th className="px-3 py-2 font-medium">
                      <MarketHelpLabel
                        label={t('mktRisk')}
                        hint={t('mktRiskHint')}
                      />
                    </th>
                    <th className="px-3 py-2 font-medium">{t('mktWhy')}</th>
                    <th className="px-3 py-2 font-medium">{t('momentum')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.account_id} className="border-t border-gray-100">
                      <td className="px-3 py-2 align-top">
                        <button
                          type="button"
                          className="font-medium text-[#00C2C2] hover:underline text-left"
                          onClick={() => setDrawerRow(row)}
                        >
                          {row.account_name}
                        </button>
                        <p className="text-[11px] text-[#5C6B7A]">
                          {row.country}
                          {row.enrolment != null ? ` · ${formatEnrolment(row.enrolment, row.enrolment_measure)}` : ''}
                        </p>
                        <SourceGap row={row} />
                      </td>
                      <td className="px-3 py-2 align-top text-[#0A1A2F]">
                        {(row.our_categories || []).map(formatCategoryLabel).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <RiskBadge row={row} />
                      </td>
                      <td className="px-3 py-2 align-top text-[#5C6B7A] max-w-md">
                        {/* Full rationale, never clamped: a two-line clamp cut the reason a client is at risk
                            mid-sentence (Edinburgh, Waterloo, Ottawa, TU Dublin — 2026-11-02, Tiago). */}
                        <span className="block whitespace-normal">{whyLine(row)}</span>
                        {whySourceUrl(row) ? (
                          <a
                            href={whySourceUrl(row)}
                            target="_blank"
                            rel="noreferrer"
                            className="block text-[11px] text-[#00C2C2] hover:underline mt-0.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t('source')}
                          </a>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-top">{formatMomentum(row.momentum_12w ?? row.momentum_4w)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <RiskDrawer
        open={Boolean(drawerRow)}
        onOpenChange={(v) => { if (!v) setDrawerRow(null); }}
        row={drawerRow}
      />
    </div>
  );
}
